import {
  Col,
  Icon,
  IconNames,
  Row,
  Typography,
  Badge,
} from '@repo/ui/components'
import { ValueWithUnit, ToDateTime } from '@repo/shared/components'
import { type MessagesKeys, useTranslate } from '@repo/shared/hooks'
import { CSSVariableColor } from '@repo/ui/types/colors'
import { UserUserAssetTransfer, useGetProjectDetails } from '@repo/services'
import { statusColorMapper, TStatus } from '../balance.item'
import { BalanceProjectTransferDetailsDialog } from './balance-project-details.transfer.dialog'

// import { BalanceDetailsDialog } from '../balance-details.dialog'

const iconMapper: Record<'debit' | 'credit', IconNames> = {
  debit: 'TriangleArrowDownLine',
  credit: 'TriangleArrowUpLine',
}

const iconColorMapper: Record<'debit' | 'credit', CSSVariableColor> = {
  debit: 'fill-positive-surface-100',
  credit: 'fill-negative-surface-100',
}

export const BalanceProjectTransferItem = ({
  data,
  showCurrency = false,
  projectId,
}: {
  data: UserUserAssetTransfer
  showCurrency?: boolean
  projectId?: string
}) => {
  const { t } = useTranslate()
  const { currency, debit, credit, createdAt, status, details } = data

  const transferType = (debit || 0) > 0 ? 'debit' : 'credit'
  const title = t(`walletPage_${transferType}` as MessagesKeys)

  const amount = (debit || 0) > 0 ? debit : credit

  const description = details?.maskedPeerPhone
    ? details?.maskedPeerPhone?.replace('+98', '0')
    : details?.maskedPeerEmail

  const { data: response } = useGetProjectDetails(projectId)
  const projectData = response?.data.project || {}

  return (
    <BalanceProjectTransferDetailsDialog data={data} projectData={projectData}>
      <Row className='justify-between gap-md py-xl cursor-pointer'>
        <Icon
          name={iconMapper[transferType as 'debit' | 'credit']}
          color={iconColorMapper[transferType as 'debit' | 'credit']}
          size='md'
        />
        <Col className='gap-xs item-start text-start cursor-pointer'>
          <Row className='gap-xs flex-wrap max-w-[150px] md:max-w-[unset] items-center'>
            <Typography
              size='sm'
              weight='bold'
              color='text-neutral-surface-100'
            >
              {title}
            </Typography>
            {showCurrency && (
              <Typography
                size='xs'
                weight='regular'
                color='text-neutral-surface-100'
              >
                {currency}
              </Typography>
            )}
            {status !== 'completed' && (
              <Badge
                variant={status === 'pending' ? 'solid' : 'liquid'}
                color={statusColorMapper[status as TStatus]}
              >
                {t(`walletPage_${status}` as MessagesKeys)}
              </Badge>
            )}
          </Row>

          <Typography size='xs' color='text-neutral-surface-100'>
            {t(`transferType_${transferType}` as MessagesKeys)}
            <bdi className='px-1'>{description}</bdi>
          </Typography>
        </Col>
        <Col className='items-end gap-xs'>
          <Typography size='xs' color='text-neutral-surface-80' variant='bdi'>
            <ToDateTime date={new Date(createdAt ?? '')} />
          </Typography>
          <ValueWithUnit
            value={amount ?? 0}
            valueSize='sm'
            unitSize='2xs'
            scale={projectData.scale}
            unit={projectData.kind === 'crypto' ? 'crypto' : 'property'}
          />
        </Col>
      </Row>
    </BalanceProjectTransferDetailsDialog>
  )
}
